'use client';
import { useState, useEffect } from 'react';
import { getAllCmsContent, getCmsTestimonials, getCmsFaqItems, type CmsLang, type CmsSectionContent, type CmsTestimonial, type CmsFaqItem } from '@/lib/cmsContent';

interface UseCmsContentReturn {
  sections: Record<string, CmsSectionContent>;
  testimonials: CmsTestimonial[];
  faqItems: CmsFaqItem[];
  loading: boolean;
  getSection: (key: string) => CmsSectionContent | null;
  getText: (sectionKey: string, field: string, fallback?: string) => string;
}

/**
 * Hook to consume published CMS content on the public landing page.
 * Falls back to provided default values if CMS content is not available.
 */
export function useCmsContent(lang: CmsLang = 'en'): UseCmsContentReturn {
  const [sections, setSections] = useState<Record<string, CmsSectionContent>>({});
  const [testimonials, setTestimonials] = useState<CmsTestimonial[]>([]);
  const [faqItems, setFaqItems] = useState<CmsFaqItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [sectionsData, testimonialsData, faqData] = await Promise.all([
          getAllCmsContent(lang),
          getCmsTestimonials(lang),
          getCmsFaqItems(lang),
        ]);
        if (!cancelled) {
          setSections(sectionsData);
          setTestimonials(testimonialsData);
          setFaqItems(faqData);
        }
      } catch {
        // Silently fail — page will use hardcoded fallbacks
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [lang]);

  const getSection = (key: string): CmsSectionContent | null => sections[key] || null;

  const getText = (sectionKey: string, field: string, fallback = ''): string => {
    return sections[sectionKey]?.[field] || fallback;
  };

  return { sections, testimonials, faqItems, loading, getSection, getText };
}
